import React, { FC, useMemo, useState } from 'react'
import { AssignmentDataVersion, AssignmentDataVersionElement, AssignmentInput, BrowserLocaleDateFormatter, Collapse, FlowAssignment, User } from 'prace-common-components'
import { useTheme } from 'styled-components'
import { useGetAssignmentVersionsQuery } from 'store/api/applications'
import { $ChangesSnapshotEmpty, $ChangesSnapshotItem, $ChangesSnapshotList, $CollapseContainer, $VersionMeta, $VersionTab, $VersionTabs } from './styles'

export const formatPreviousValues = (input?: AssignmentInput): string => {
  const values = (input?.values || []).filter((v) => v != null && String(v).trim() !== '')
  return values.length ? values.join(', ') : ''
}

const renderVersionElements = (elements: AssignmentDataVersionElement[]): JSX.Element => (
  <$ChangesSnapshotList>
    {elements.map((el, idx) => {
      const value = el.uploadFilename || formatPreviousValues({ values: el.values } as AssignmentInput)
      return <$ChangesSnapshotItem key={`${el.flowElementId}-${el.dataGroupId ?? 'x'}-${idx}`}>
        <b>{el.title || `#${el.flowElementId}`}: </b>
        {value || <$ChangesSnapshotEmpty>(empty)</$ChangesSnapshotEmpty>}
      </$ChangesSnapshotItem>
    })}
  </$ChangesSnapshotList>
)

/* Version switcher for one assignment: the stored snapshots plus a Current tab built from the
 * live AssignmentData. Own component so the element merge below is memoized per assignment
 * instead of re-running on every render of the whole application page. */
type Props = {
  assignment: FlowAssignment,
  timelineUsers: User[],
  applicationId?: number,
}

export const AssignmentVersions: FC<Props> = ({ assignment, timelineUsers, applicationId }) => {
  const theme = useTheme() as { colors: { secondary: string }}
  const [open, setOpen] = useState(false)
  const [selIdx, setSelIdx] = useState(-1)
  /* The application payload carries version metadata only; the snapshots are fetched on open so
     the (unbounded, jsonb) element history stays off every application load */
  const { data: snapshots = [], isFetching } = useGetAssignmentVersionsQuery(applicationId as number,
    { skip: !open || !applicationId })
  /* The endpoint returns every visible assignment's versions for the application (one cached
     request shared by all the panels), so narrow to this assignment's */
  const elementsById = useMemo(
    () => new Map(snapshots
      .filter((v) => v.assignmentId === assignment.id)
      .map((v) => [v.id, v.elements || []])),
    [snapshots, assignment.id],
  )

  /* Current version: pull the latest live values for every element versioned so far.
     Unions all versions (a version only holds the fields sent back that round, so the
     latest one alone would drop fields versioned earlier), keeping the most recent
     entry per element+group. Group instance nuances are out of scope here. */
  const currentElements = useMemo<AssignmentDataVersionElement[]>(() => {
    const versionedElements = [...new Map([...elementsById.values()]
      .flat()
      .map((el) => [`${el.flowElementId}-${el.dataGroupId ?? 'x'}`, el]),
    ).values()]
    return versionedElements.map((el) => {
      const data = assignment.data?.find((d) =>
        d.flowElementId === el.flowElementId &&
        (d.dataGroupId ?? null) === (el.dataGroupId ?? null))
      return {
        ...el,
        values: data?.input?.values || [],
        uploadFilename: data?.upload?.originalFilename ?? null,
      }
    })
  }, [elementsById, assignment.data])

  const versions = assignment.versions || []
  if(!versions.length) return null

  const selectedVer = selIdx >= 0 ? versions[selIdx] : null
  const metaUser = selectedVer ? timelineUsers.find((u) => u.id === selectedVer.submittedByUserId) : null

  return <$CollapseContainer>
    <Collapse
      title='Versions'
      open={open}
      handleOpen={setOpen}
      icon='circle'
      iconProps={{color: theme.colors.secondary, size: 10, clickable: false}}
    >
      <$VersionTabs>
        {versions.map((v: AssignmentDataVersion, idx: number) =>
          <$VersionTab key={v.id} type='button' selected={selIdx === idx} onClick={() => setSelIdx(idx)}>
            {`V${v.versionNumber}`}
          </$VersionTab>,
        )}
        <$VersionTab type='button' selected={selIdx === -1} onClick={() => setSelIdx(-1)}>
          Current
        </$VersionTab>
      </$VersionTabs>
      {selectedVer
        ? <>
          <$VersionMeta>
            {`Version ${selectedVer.versionNumber}`}
            {selectedVer.submittedAt ? ` · submitted ${BrowserLocaleDateFormatter.format(new Date(selectedVer.submittedAt))}` : ''}
            {metaUser ? ` by ${`${metaUser.firstName || ''} ${metaUser.lastName || ''}`.trim() || metaUser.email}` : ''}
          </$VersionMeta>
          {renderVersionElements(elementsById.get(selectedVer.id) || [])}
        </>
        : <>
          <$VersionMeta>Current version</$VersionMeta>
          {currentElements.length
            ? renderVersionElements(currentElements)
            : !isFetching && <$ChangesSnapshotEmpty>No versioned fields</$ChangesSnapshotEmpty>}
        </>
      }
    </Collapse>
  </$CollapseContainer>
}
